import {CurrencyPipe, DecimalPipe, TitleCasePipe, registerLocaleData} from '@angular/common'
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http'
import localeCa from '@angular/common/locales/ca'
import localeDe from '@angular/common/locales/de'
import localeEn from '@angular/common/locales/en'
import localeEs from '@angular/common/locales/es'
import localeFr from '@angular/common/locales/fr'
import localeIt from '@angular/common/locales/it'
import localePt from '@angular/common/locales/pt'
import localeZh from '@angular/common/locales/zh'
import {
  type EnvironmentProviders,
  LOCALE_ID,
  type Provider,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core'
import {provideNativeDateAdapter} from '@angular/material/core'
import {Title} from '@angular/platform-browser'
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async'
import {RouteReuseStrategy} from '@angular/router'
import {Geolocation} from '@awesome-cordova-plugins/geolocation/ngx'
import {Globalization} from '@awesome-cordova-plugins/globalization/ngx'
import {InAppBrowser} from '@awesome-cordova-plugins/in-app-browser/ngx'
import {NativeGeocoder} from '@awesome-cordova-plugins/native-geocoder/ngx'
import {PhotoViewer} from '@awesome-cordova-plugins/photo-viewer/ngx'
import {IonicRouteStrategy, provideIonicAngular} from '@ionic/angular/standalone'
import {defineCustomElements} from '@ionic/pwa-elements/loader'
import {IonicStorageModule} from '@ionic/storage-angular'
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core'
import {Deploy} from 'cordova-plugin-ionic/dist/ngx'
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module} from 'ng-recaptcha'
import {MomentModule} from 'ngx-moment'
import {Socket} from 'ngx-socket-io'
import {passiveSupport} from 'passive-events-support/src/utils'
import {firstValueFrom} from 'rxjs'
import {register} from 'swiper/element/bundle'
import {CapitalizePipe} from '@cart/src/app/pipes/capitalize.pipe'
import {DateUtils} from '@cart/src/app/services/utils/DateUtils'
import {ANALYTICS_PROVIDERS} from '@services/analytics/analytics-provider.interface'
import {FirebaseAnalyticsProvider} from '@services/analytics/firebase-analytics.provider'
import {GoogleAnalyticsProvider} from '@services/analytics/google-analytics.provider'
import {PostHogAnalyticsProvider} from '@services/analytics/posthog-analytics.provider'
import {PostHogCapacitorAnalyticsProvider} from '@services/analytics/posthog-capacitor-analytics.provider'
import {DeeplinkService} from '@services/deeplink.service'
import {deployFactory} from '@services/deploy-factory'
import {EventLoggerService} from '@services/event-logger.service'
import {LocalizationService} from '@services/gps/localizations.service'
import {NeterrorInterceptor} from '@services/interceptors/neterror-interceptor'
import {UveSocket} from '@services/socket/uve-socket'
import {UveStorageService} from '@services/uve-storage.service'
import {
  SESSION_SERVICE_TOKEN,
  SLAVE_SESSION_SERVICE_TOKEN,
  SessionService,
  provideSlaveSessionService,
} from '@uve-solutions/composition/session'
import {ChatContract} from '@uve-solutions/f-chat/core'
import {provideUveUi} from '@uve-solutions/ui/setup'
import {environment} from '../environments/environment'
import {SHARED_CORE_EVENT_BUS, coreEventBusProviders} from './core/core-event-bus'
import {orgCalendarTranslation} from './core/i18n/org-calendar-translation'
import {provideUveI18n} from './core/i18n/uve-i18n.providers'
import {registerIcons} from './icons'
import {ChatFacade} from './pages/chat/chat.facade'
import {AppSessionService} from './services/app-session.service'
import {AddCustomHeadersInterceptor} from './services/interceptors/add-custom-headers-interceptor'
import {WebpackTranslateLoader} from './share'

registerLocaleData(localeEn, 'en')
registerLocaleData(localeEs, 'es')
registerLocaleData(localeCa, 'ca')
registerLocaleData(localeFr, 'fr')
registerLocaleData(localeDe, 'de')
registerLocaleData(localeIt, 'it')
registerLocaleData(localePt, 'pt')
registerLocaleData(localeZh, 'zh')

passiveSupport({events: ['wheel', 'mousewheel', 'touchstart']})

defineCustomElements(window)

registerIcons()

register()

export function appInitializerFactory(translateService: TranslateService): () => Promise<unknown> {
  return () => {
    return firstValueFrom(translateService.use(translateService.getBrowserLang() || 'en'))
  }
}

export function provideAppCore(provideAppRouter: () => EnvironmentProviders): (Provider | EnvironmentProviders)[] {
  return [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({eventCoalescing: true}),
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    provideIonicAngular({swipeBackEnabled: false, animated: false}),
    provideAppRouter(),
    provideHttpClient(withInterceptorsFromDi()),
    {provide: HTTP_INTERCEPTORS, useClass: AddCustomHeadersInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: NeterrorInterceptor, multi: true},
    provideAnimationsAsync(),
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(TranslateService))
      return initializerFn()
    }),
    provideAppInitializer(async () => {
      const uveStorageService = inject(UveStorageService)
      await uveStorageService.init()
    }),
    importProvidersFrom(
      IonicStorageModule.forRoot({name: '__mydb'}),
      TranslateModule.forRoot({
        loader: {provide: TranslateLoader, useClass: WebpackTranslateLoader, deps: [HttpClient]},
      }),
      RecaptchaV3Module,
      MomentModule
    ),
    {provide: LOCALE_ID, useValue: 'es'},
    provideNativeDateAdapter(),
    ...provideSlaveSessionService(
      {
        authBaseUrl: environment.apiUrl,
      },
      SHARED_CORE_EVENT_BUS
    ),
    AppSessionService,
    {provide: SessionService, useExisting: AppSessionService},
    {provide: SESSION_SERVICE_TOKEN, useExisting: AppSessionService},
    {provide: SLAVE_SESSION_SERVICE_TOKEN, useExisting: AppSessionService},
    {provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.siteKey},
    coreEventBusProviders,
    {provide: ChatContract, useClass: ChatFacade},
    InAppBrowser,
    PhotoViewer,
    Globalization,
    Geolocation,
    LocalizationService,
    NativeGeocoder,
    {provide: Deploy, useFactory: deployFactory},
    {provide: DecimalPipe, useClass: DecimalPipe},
    {provide: CurrencyPipe, useClass: CurrencyPipe},
    {provide: TitleCasePipe, useClass: TitleCasePipe},
    {
      provide: ANALYTICS_PROVIDERS,
      useFactory: () => [
        inject(GoogleAnalyticsProvider),
        inject(FirebaseAnalyticsProvider),
        inject(PostHogAnalyticsProvider),
        inject(PostHogCapacitorAnalyticsProvider),
      ],
    },
    EventLoggerService,
    DeeplinkService,
    DateUtils,
    Title,
    UveSocket,
    {provide: Socket, useClass: UveSocket},
    CapitalizePipe,
    provideUveUi({translation: orgCalendarTranslation}),
    provideUveI18n(),
  ]
}
